import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  HeartHandshake,
  Lightbulb,
  BookOpen,
  ArrowRight,
} from "lucide-react";

const AboutPage = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-[#e2e8f0]/40"
            style={{
              width: `${Math.random() * 100 + 30}px`,
              height: `${Math.random() * 100 + 30}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              x: [0, 15, 0],
            }}
            transition={{
              duration: 15 + Math.random() * 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-gray-800 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            About Us
          </motion.h2>

          <motion.h3
            className="text-2xl md:text-3xl font-semibold text-red-500"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            AAIDAN K LTD
          </motion.h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <motion.div
            className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 h-full"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center mb-6">
              <div className="bg-red-100 p-3 rounded-xl mr-4">
                <Users className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Our Approach</h3>
            </div>

            <div className="space-y-4 text-gray-600">
              <p>
                We understand that financial jargon and figures can be
                intimidating. For this reason, we have established an accounting
                business that treats you as more than just a customer. At AAIDAN
                K LTD, we take the time to comprehend your individual narrative,
                obstacles, and objectives while combining professional financial
                advice with sincere concern.
              </p>

              <p>
                We are here to make bookkeeping, VAT, payroll, and year-end
                accounts easy, clear, and even empowering for you. Consider us
                your reliable co-pilot, we take care of the details so you can
                confidently concentrate on expanding your company and perhaps
                even enjoy the ride.
              </p>
            </div>
          </motion.div>

          <motion.div
            className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 h-full"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="flex items-center mb-6">
              <div className="bg-red-100 p-3 rounded-xl mr-4">
                <Lightbulb className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">
                What makes us unique?
              </h3>
            </div>

            <div className="space-y-4 text-gray-600">
              <p>
                Even though we have a lot of technical talents, it goes beyond
                that. It's the way we operate warmly, adaptably, and with
                genuine dialogue. No prefabricated answers, no automated
                guidance. Simply a group that pays attention, adjusts, and helps
                you through all of your financial challenges.
              </p>

              <p>
                You deserve a partner that celebrates your successes, helps you
                overcome obstacles, and makes money seem less like a chore. You
                deserve more than spreadsheets and compliance. Together, let's
                make it happen.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative elements */}
      <motion.div
        className="absolute top-20 left-0 w-64 h-64 rounded-full bg-red-100/30 blur-3xl z-0"
        animate={{
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="absolute bottom-20 right-0 w-56 h-56 rounded-full bg-blue-100/30 blur-3xl z-0"
        animate={{
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />
    </section>
  );
};

export default AboutPage;
